"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.summarizeOrCadDsnCfbStream = summarizeOrCadDsnCfbStream;
const DsnCfbBinarySpec_1 = require("./DsnCfbBinarySpec");
const OrcadDsnCfbRecordDecoders_1 = require("./OrcadDsnCfbRecordDecoders");
const OrcadDsnCfbStreamSpecs_1 = require("./OrcadDsnCfbStreamSpecs");
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
const OrcadDsnCfbCacheSourceLibraries_1 = require("./OrcadDsnCfbCacheSourceLibraries");
const OrcadDsnCfbHierarchyPhysicalNames_1 = require("./OrcadDsnCfbHierarchyPhysicalNames");
const OrcadDsnCfbOccurrenceFrames_1 = require("./OrcadDsnCfbOccurrenceFrames");
const OrcadDsnCfbHierarchyScopes_1 = require("./OrcadDsnCfbHierarchyScopes");
const OrcadDsnCfbPayloadIndex_1 = require("./OrcadDsnCfbPayloadIndex");
function stringsInPayload(bytes) {
    return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
        maxCount: 64,
        maxLength: 512,
        requireNullTerminator: true,
    });
}
function parseDsnStream(bytes) {
    const jsonOffset = bytes.indexOf(0x7b);
    const jsonEnd = bytes.lastIndexOf(0x7d);
    if (jsonOffset === -1 || jsonEnd <= jsonOffset) {
        return undefined;
    }
    try {
        const text = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, jsonOffset, jsonEnd + 1);
        return {
            kind: 'dsn-stream',
            jsonOffset,
            json: JSON.parse(text),
        };
    }
    catch {
        return undefined;
    }
}
function viewNameFromSchematicPath(path) {
    const match = /^Root Entry\/Views\/([^/]+)\/Schematic$/.exec(path);
    return match?.[1];
}
function parseSchematicStream(path, bytes, strings, pageStreamNames) {
    const viewName = viewNameFromSchematicPath(path);
    if (!viewName || strings.length === 0) {
        return undefined;
    }
    const schematicName = strings[0]?.value;
    const pageNames = strings
        .slice(1)
        .map((entry) => entry.value)
        .filter((value) => pageStreamNames.has(`Root Entry/Views/${viewName}/Pages/${value}`));
    let pageCountHint;
    if (strings[0]) {
        const firstStringEnd = strings[0].offset + 2 + strings[0].length + (strings[0].hasNullTerminator ? 1 : 0);
        const scanEnd = Math.min(firstStringEnd + 16, bytes.length - 1);
        for (let offset = firstStringEnd; offset < scanEnd; offset++) {
            const candidate = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
            if (candidate === pageNames.length) {
                pageCountHint = candidate;
                break;
            }
        }
    }
    return {
        kind: 'schematic',
        viewName,
        schematicName,
        pageCountHint,
        pageNames,
    };
}
const INSTANCE_MARKER_PATTERN = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.LIBRARY_INSTANCE_MARKER_PATTERN;
const isDecodedObjectId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.isDecodedObjectId;
function recordedRootNameFromLibraryTail(bytes) {
    if (bytes.length < 4 || bytes[bytes.length - 1] !== 0) {
        return undefined;
    }
    const earliestOffset = Math.max(0, bytes.length - 0xffff - 3);
    const candidates = [];
    for (let offset = earliestOffset; offset <= bytes.length - 4; offset++) {
        const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset);
        if (offset + 2 + length + 1 !== bytes.length) {
            continue;
        }
        const field = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, offset, {
            maxLength: 0xffff,
            requireNullTerminator: true,
        });
        if (field !== undefined) {
            candidates.push(field.value);
        }
    }
    return candidates.length === 1 ? candidates[0] : undefined;
}
function parseLibraryStream(path, bytes, diagnostics) {
    if (path !== 'Root Entry/Library') {
        return undefined;
    }
    const strings = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
        maxCount: Number.MAX_SAFE_INTEGER,
    });
    const uniqueStrings = new Set(strings.map((string) => string.value));
    const stringCatalog = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLibraryStringCatalog(bytes);
    const recordedRootName = recordedRootNameFromLibraryTail(bytes);
    return {
        kind: 'library',
        countedStringCount: strings.length,
        uniqueStringCount: uniqueStrings.size,
        propertyNames: [],
        firstStrings: strings.slice(0, 50),
        componentCandidateRecords: componentCandidateRecordsFromLibraryStrings(strings, path, diagnostics),
        ...(stringCatalog ? { stringCatalog } : {}),
        ...(recordedRootName !== undefined ? { recordedRootName } : {}),
    };
}
function componentCandidateRecordsFromLibraryStrings(strings, streamPath, diagnostics) {
    const records = [];
    let blockStart = 0;
    let blockValues = [];
    let insideRun = false;
    for (let index = 0; index < strings.length; index++) {
        const string = strings[index];
        if (!INSTANCE_MARKER_PATTERN.test(string.value)) {
            if (insideRun) {
                blockStart = index;
                insideRun = false;
            }
            continue;
        }
        if (!insideRun) {
            blockValues = strings.slice(blockStart, index).map((entry) => entry.value);
            insideRun = true;
        }
        records.push({
            offset: string.offset,
            name: string.value,
            values: [...blockValues, string.value],
            properties: [],
        });
    }
    if (records.length > 0) {
        diagnostics.push({
            severity: 'warning',
            code: 'library-catalog-columns-unnamed',
            message: `Library catalog column names could not be decoded; ${records.length} instance rows carry unnamed values`,
            streamPath,
        });
    }
    return records;
}
function parseDirectoryStream(path, bytes) {
    const directoryKind = path === 'Root Entry/Parts Directory' ? 'parts-directory'
        : path === 'Root Entry/Packages Directory' ? 'packages-directory'
            : undefined;
    if (!directoryKind) {
        return undefined;
    }
    const entries = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(bytes, {
        maxCount: Number.MAX_SAFE_INTEGER,
        maxLength: 512,
    }).map((string) => {
        const viewSuffix = /^(.*)\.([^.]+)$/.exec(string.value);
        return {
            offset: string.offset,
            name: string.value,
            ...(directoryKind === 'parts-directory' && viewSuffix ?
                {
                    packageName: viewSuffix[1],
                    viewName: viewSuffix[2],
                }
                : {}),
        };
    });
    return {
        kind: directoryKind,
        entries,
        entryCount: entries.length,
    };
}
function parseVariantTextStream(path, asciiRuns) {
    if (!path.includes('/VariantStore/') || asciiRuns.length === 0) {
        return undefined;
    }
    const rows = asciiRuns
        .map((run) => ({
        offset: run.offset,
        values: run.value.includes('^') ? run.value.split('^') : [run.value],
    }))
        .filter((row) => row.values.length > 0 && row.values.some((value) => value.length > 0));
    if (rows.length === 0) {
        return undefined;
    }
    let currentHeader;
    for (const row of rows) {
        if (row.values.length > 1 && row.values.some((value) => /part|footprint|value|name/i.test(value))) {
            currentHeader = row.values;
            continue;
        }
        if (currentHeader && row.values.length === currentHeader.length) {
            row.properties = currentHeader
                .map((name, index) => ({
                name,
                value: row.values[index] ?? '',
                source: 'variant-update',
            }))
                .filter((property) => property.value.length > 0);
        }
    }
    return {
        kind: 'variant-text',
        rows,
        rowCount: rows.length,
        ...(currentHeader ? { header: currentHeader } : {}),
    };
}
function parseHierarchyStream(path, bytes, diagnostics) {
    const match = /^Root Entry\/Views\/([^/]+)\/Hierarchy\/Hierarchy$/.exec(path);
    if (!match) {
        return undefined;
    }
    const entries = [];
    const records = [];
    const payloads = new Map();
    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => payloads.set(payload.offset, payload));
    const payloadIndex = new OrcadDsnCfbPayloadIndex_1.OrcadDsnCfbPayloadIndex([...payloads.values()]);
    const ownershipBranches = [];
    const parentScopes = new Map();
    const owningViews = (0, OrcadDsnCfbHierarchyScopes_1.readHierarchyOccurrenceScopes)(bytes, match[1], diagnostics, path, ownershipBranches, parentScopes);
    const inlineChildContainers = new Set();
    for (const kindOffset of parentScopes.keys()) {
        const container = payloadIndex.findUniqueContainingPayload(kindOffset - 8);
        if (container !== undefined && container.offset !== kindOffset - 8) {
            inlineChildContainers.add(container.offset);
        }
    }
    OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.forEachTerminatedPayload(bytes, (payload) => {
        const strings = stringsInPayload(payload.payload);
        const recordDbId = payload.payload.length >= 8 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 4) : undefined;
        const firstString = strings[0];
        records.push({
            recordId: payload.payload.length >= 4 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0) : 0,
            ...(isDecodedObjectId(recordDbId) ? { dbId: recordDbId } : {}),
            ...(firstString ? { name: firstString.value, nameOffset: firstString.offset } : {}),
        });
        const nameString = strings.find((string) => string.offset >= 8);
        if (payload.payload[8] === 0x42) {
            const occurrence = (0, OrcadDsnCfbOccurrenceFrames_1.readHierarchyOccurrenceFrame)(bytes, payload, payloads, owningViews.get(payload.offset)?.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved);
            if (occurrence) {
                entries.push({
                    offset: payload.offset,
                    recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
                    occurrenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
                    refInstId: recordDbId,
                    dbId: recordDbId,
                    name: occurrence.name,
                    referenceStringOffset: occurrence.nameOffset,
                    sectionSelectorStringIndex: occurrence.selectorIndex,
                    owningViewName: owningViews.get(payload.offset)?.viewName,
                    ownershipBranchOffset: owningViews.get(payload.offset)?.branchOffset,
                    ownershipState: owningViews.get(payload.offset)?.state ?? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
                });
                return;
            }
            else {
                diagnostics.push({
                    severity: 'warning',
                    code: 'hierarchy-occurrence-frame-withheld',
                    streamPath: path,
                    offset: payload.offset,
                    message: `CFB occurrence [hierarchy-occurrence-frame-withheld] refInstId=${recordDbId}: incomplete or unsupported occurrence frame`,
                });
                const scope = owningViews.get(payload.offset);
                if (scope !== undefined) {
                    entries.push({
                        offset: payload.offset,
                        recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0),
                        ...(isDecodedObjectId(recordDbId) ? { refInstId: recordDbId } : {}),
                        owningViewName: scope?.viewName,
                        ownershipBranchOffset: scope?.branchOffset,
                        ownershipState: scope?.state ?? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
                    });
                    return;
                }
            }
        }
        if (!nameString) {
            return;
        }
        const referenceString = strings.find((string) => string.offset > nameString.offset && string.value.length > 1 && string.value !== 'X');
        const dbId = payload.payload.length >= 8 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 4) : undefined;
        const recordId = payload.payload.length >= 4 ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(payload.payload, 0) : 0;
        const parent = parentScopes.get(payload.offset + 8);
        const child = ownershipBranches.find((branch) => branch.offset === payload.offset + 8);
        if (parent !== undefined && child?.viewName !== undefined) {
            entries.push({
                offset: payload.offset,
                recordId,
                ...(isDecodedObjectId(dbId) ? { dbId } : {}),
                name: child.viewName,
                contentsViewName: child.viewName,
                referenceStringOffset: child.offset + 9,
                parentViewName: parent.viewName,
                parentBranchOffset: parent.branchOffset,
            });
            return;
        }
        if (inlineChildContainers.has(payload.offset)) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-payload-reference-withheld',
                streamPath: path,
                offset: payload.offset,
                message: 'CFB hierarchy: enclosing payload is not a child header; inline child strings cannot supply its reference or identity',
            });
            return;
        }
        entries.push({
            offset: payload.offset,
            recordId,
            ...(isDecodedObjectId(dbId) ? { dbId } : {}),
            name: nameString.value,
            ...(referenceString ? { reference: referenceString.value } : {}),
            contentsViewName: nameString.value,
            ...(parent === undefined ? {} : { parentViewName: parent.viewName, parentBranchOffset: parent.branchOffset }),
        });
    });
    for (const [kindOffset, parent] of parentScopes) {
        const offset = kindOffset - 8;
        if (entries.some((entry) => entry.offset === offset && entry.parentBranchOffset !== undefined)) {
            continue;
        }
        const child = ownershipBranches.find((branch) => branch.offset === kindOffset);
        const container = payloadIndex.findUniqueContainingPayload(offset);
        if (child?.viewName === undefined ||
            container === undefined ||
            kindOffset + 11 > container.offset + container.payloadByteLength) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-child-container-withheld',
                streamPath: path,
                offset,
                message: 'CFB hierarchy: counted child header has no decoded name or unique enclosing payload; child reference withheld',
            });
            continue;
        }
        const dbId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
        entries.push({
            offset,
            recordId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset),
            ...(isDecodedObjectId(dbId) ? { dbId } : {}),
            name: child.viewName,
            contentsViewName: child.viewName,
            referenceStringOffset: kindOffset + 9,
            parentViewName: parent.viewName,
            parentBranchOffset: parent.branchOffset,
        });
    }
    for (const [offset, scope] of owningViews) {
        if (payloads.has(offset)) {
            continue;
        }
        const container = payloadIndex.findUniqueContainingPayload(offset);
        const recordId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
        const refInstId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
        if (container === undefined) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-occurrence-container-withheld',
                streamPath: path,
                offset,
                message: 'CFB occurrence: counted child has no unique enclosing payload; ownership tombstone retained',
            });
            entries.push({
                offset,
                recordId,
                ...(isDecodedObjectId(refInstId) ? { refInstId } : {}),
                owningViewName: scope.viewName,
                ownershipBranchOffset: scope.branchOffset,
                ownershipState: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Withheld,
            });
            continue;
        }
        const payload = {
            offset,
            payloadByteLength: container.offset + container.payloadByteLength - offset,
            byteLength: container.offset + container.byteLength - offset,
            payload: bytes.subarray(offset, container.offset + container.payloadByteLength),
        };
        const occurrence = (0, OrcadDsnCfbOccurrenceFrames_1.readHierarchyOccurrenceFrame)(bytes, payload, payloads, scope.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved);
        if (!occurrence) {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-occurrence-frame-withheld',
                streamPath: path,
                offset,
                message: 'CFB occurrence: structurally owned inline occurrence has incomplete annotation framing',
            });
        }
        entries.push({
            offset,
            recordId,
            ...(occurrence !== undefined || isDecodedObjectId(recordId) ? { occurrenceId: recordId } : {}),
            ...(occurrence !== undefined || isDecodedObjectId(refInstId) ? { refInstId, dbId: refInstId } : {}),
            name: occurrence?.name,
            referenceStringOffset: occurrence?.nameOffset,
            sectionSelectorStringIndex: occurrence?.selectorIndex,
            owningViewName: scope.viewName,
            ownershipBranchOffset: scope.branchOffset,
            ownershipState: scope.state,
        });
    }
    entries.sort((left, right) => left.offset - right.offset);
    return {
        kind: 'hierarchy',
        ownershipBranches,
        viewName: match[1],
        physicalNetNames: (0, OrcadDsnCfbHierarchyPhysicalNames_1.readHierarchyPhysicalNetNames)(bytes, match[1], path, diagnostics),
        entries,
        entryCount: entries.length,
        records,
    };
}
function readCisStringList(bytes, offset, count, maxLength) {
    if (count < 0 || count > 512) {
        return undefined;
    }
    const values = [];
    let cursor = offset;
    for (let index = 0; index < count; index++) {
        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength });
        if (!string) {
            return undefined;
        }
        values.push(string.value);
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string);
    }
    return { values, offset: cursor };
}
function readCisPropertyValues(bytes, offset, count, maxLength) {
    if (count < 0 || count > 512) {
        return undefined;
    }
    const values = [];
    let cursor = offset;
    for (let index = 0; index < count; index++) {
        if (cursor + 2 <= bytes.length && OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor) === 0) {
            values.push('');
            cursor += cursor + 2 < bytes.length && bytes[cursor + 2] === 0 ? 3 : 2;
            continue;
        }
        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength });
        if (!string) {
            return undefined;
        }
        values.push(string.value);
        cursor = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string);
    }
    return { values, offset: cursor };
}
function parseCisSchematicRecord(bytes, offset) {
    const start = offset;
    if (offset + 16 > bytes.length) {
        return undefined;
    }
    const recordType = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    const recordId = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
    const occurrenceNameCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 8);
    offset += 12;
    if (recordType > 10_000 || recordId > 1_000_000_000 || occurrenceNameCount < 1 || occurrenceNameCount > 128) {
        return undefined;
    }
    const occurrenceNames = readCisStringList(bytes, offset, occurrenceNameCount, 512);
    if (!occurrenceNames || occurrenceNames.offset + 4 > bytes.length) {
        return undefined;
    }
    offset = occurrenceNames.offset;
    const qualifierCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    offset += 4;
    const qualifiers = readCisStringList(bytes, offset, qualifierCount, 512);
    if (!qualifiers || qualifiers.offset + 8 > bytes.length) {
        return undefined;
    }
    offset = qualifiers.offset;
    const propertyGroup = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 4);
    offset += 8;
    if (propertyGroup > 10_000 || propertyCount < 1 || propertyCount > 512) {
        return undefined;
    }
    const propertyNames = readCisStringList(bytes, offset, propertyCount, 512);
    if (!propertyNames || propertyNames.offset + 4 > bytes.length) {
        return undefined;
    }
    offset = propertyNames.offset;
    const propertyValueCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    offset += 4;
    if (propertyValueCount !== propertyNames.values.length) {
        return undefined;
    }
    const propertyValues = readCisPropertyValues(bytes, offset, propertyValueCount, 4096);
    if (!propertyValues) {
        return undefined;
    }
    offset = propertyValues.offset;
    const properties = propertyNames.values.map((name, index) => ({
        name,
        value: propertyValues.values[index] ?? '',
    }));
    return {
        offset: start,
        byteLength: offset - start,
        recordType,
        recordId,
        occurrenceNames: occurrenceNames.values,
        qualifiers: qualifiers.values,
        propertyGroup,
        propertyNames: propertyNames.values,
        properties,
        populatedProperties: properties.filter((property) => property.value.length > 0),
    };
}
function cisPropertyRecordFromNamesAndValues(offset, byteLength, recordType, recordId, occurrenceNames, qualifiers, propertyGroup, propertyNames, propertyValues) {
    const properties = propertyNames.map((name, index) => ({
        name,
        value: propertyValues[index] ?? '',
    }));
    return {
        offset,
        byteLength,
        recordType,
        recordId,
        occurrenceNames,
        qualifiers,
        propertyGroup,
        propertyNames,
        properties,
        populatedProperties: properties.filter((property) => property.value.length > 0),
    };
}
function parseCisSchematicPropertyOnlyRecord(bytes, offset) {
    const start = offset;
    if (offset + 4 > bytes.length) {
        return undefined;
    }
    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    offset += 4;
    if (propertyCount < 1 || propertyCount > 512) {
        return undefined;
    }
    const propertyNames = readCisStringList(bytes, offset, propertyCount, 512);
    if (!propertyNames || propertyNames.offset + 4 > bytes.length) {
        return undefined;
    }
    offset = propertyNames.offset;
    const propertyValueCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset);
    offset += 4;
    if (propertyValueCount !== propertyNames.values.length) {
        return undefined;
    }
    const propertyValues = readCisPropertyValues(bytes, offset, propertyValueCount, 4096);
    if (!propertyValues) {
        return undefined;
    }
    offset = propertyValues.offset;
    return cisPropertyRecordFromNamesAndValues(start, offset - start, 0, 0, [], [], 0, propertyNames.values, propertyValues.values);
}
function parseCisSchematicInfoStream(path, bytes) {
    const match = /^Root Entry\/Views\/([^/]+)\/CISSchematic\/SchematicInfoStorage\/([^/]+)$/.exec(path);
    if (!match || match[2] === 'SchematicInfoStream') {
        return undefined;
    }
    const records = [];
    let offset = 0;
    while (offset < bytes.length) {
        const record = parseCisSchematicRecord(bytes, offset) ?? parseCisSchematicPropertyOnlyRecord(bytes, offset);
        if (!record) {
            break;
        }
        records.push(record);
        offset += record.byteLength;
    }
    if (records.length === 0) {
        return undefined;
    }
    return {
        kind: 'cis-schematic-info',
        viewName: match[1],
        pageName: match[2],
        recordCount: records.length,
        propertyNames: Array.from(new Set(records.flatMap((record) => record.propertyNames))).sort(),
        records,
    };
}
function primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
    return {
        countedStringCount: strings.length,
        asciiRunCount: asciiRuns.length,
        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPrimitives(decodedPrimitives.counts) ?
            {
                decodedPrimitiveCounts: decodedPrimitives.counts,
                decodedPrimitiveByteCount: decodedPrimitives.decodedByteCount,
            }
            : {}),
        ...(decodedSymbolObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects(decodedSymbolObjects.counts) ?
            {
                decodedSymbolObjectCounts: decodedSymbolObjects.counts,
                decodedSymbolObjectByteCount: decodedSymbolObjects.decodedByteCount,
            }
            : {}),
    };
}
function pageObjectStreamSummary(decodedPageObjects) {
    return {
        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPageObjects(decodedPageObjects.counts) ?
            {
                decodedPageObjectCounts: decodedPageObjects.counts,
                decodedPageObjectByteCount: decodedPageObjects.decodedByteCount,
            }
            : {}),
    };
}
function parsePageHeader(bytes, strings) {
    const pageName = strings[0];
    if (!pageName) {
        return {};
    }
    const pageNameEnd = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pageName);
    const pageSize = strings.find((string) => string.offset === pageNameEnd && string.length <= 16);
    if (!pageSize) {
        return {};
    }
    const metadataOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(pageSize);
    const pageDateTime = metadataOffset + 8 <= bytes.length ?
        {
            createDateEpoch: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset),
            modifyDateEpoch: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 4),
        }
        : undefined;
    const pageSizeInfo = metadataOffset + 36 <= bytes.length ?
        {
            isMetric: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 20) !== 0,
            width: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 24),
            height: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 28),
            pinToPin: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, metadataOffset + 32),
        }
        : undefined;
    return {
        pageSize: pageSize.value,
        ...(pageDateTime && pageDateTime.createDateEpoch > 0 && pageDateTime.modifyDateEpoch > 0 ? { pageDateTime } : {}),
        ...((pageSizeInfo &&
            pageSizeInfo.width > 0 &&
            pageSizeInfo.height > 0 &&
            pageSizeInfo.pinToPin > 0 &&
            pageSizeInfo.width <= 1_000_000 &&
            pageSizeInfo.height <= 1_000_000 &&
            pageSizeInfo.pinToPin <= 10_000) ?
            { pageSizeInfo }
            : {}),
    };
}
function parseCacheStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
    if (path !== 'Root Entry/Cache') {
        return undefined;
    }
    return {
        kind: 'cache',
        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
    };
}
function parsePackageStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
    const match = /^Root Entry\/Packages\/([^/]+)$/.exec(path);
    if (!match) {
        return undefined;
    }
    return {
        kind: 'package',
        packageName: match[1],
        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
    };
}
function parseSymbolStream(path, strings, asciiRuns, decodedPrimitives, decodedSymbolObjects) {
    const match = /^Root Entry\/Symbols\/([^/]+)$/.exec(path);
    if (!match) {
        return undefined;
    }
    return {
        kind: 'symbol',
        symbolName: match[1],
        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives, decodedSymbolObjects),
    };
}
function parsePageStream(path, bytes, strings, asciiRuns, decodedPrimitives, decodedPageObjects) {
    const match = /^Root Entry\/Views\/([^/]+)\/Pages\/([^/]+)$/.exec(path);
    if (!match) {
        return undefined;
    }
    return {
        kind: 'page',
        viewName: match[1],
        pageName: strings[0]?.value ?? match[2],
        ...parsePageHeader(bytes, strings),
        ...primitiveStreamSummary(strings, asciiRuns, decodedPrimitives),
        ...pageObjectStreamSummary(decodedPageObjects),
    };
}
function parseTextStream(path, asciiRuns) {
    if (!/\.txt$/i.test(path) || asciiRuns.length === 0) {
        return undefined;
    }
    return {
        kind: 'text',
        text: asciiRuns.map((run) => run.value).join('\n'),
    };
}
function summarizeOrCadDsnCfbStream(entry, pageStreamNames, options, libraryStringCatalog) {
    if (entry.type !== 2 || !entry.content) {
        return undefined;
    }
    const streamSpecs = DsnCfbBinarySpec_1.DsnCfbBinarySpec.matchStreamSpecs(OrcadDsnCfbStreamSpecs_1.ORCAD_DSN_CFB_STREAM_SPECS, entry);
    const leadingRecords = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.detectLeadingRecordHeaders(entry.content);
    const lengthPrefixedStrings = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(entry.content, {
        maxCount: Number.MAX_SAFE_INTEGER,
    });
    const diagnosticAsciiRuns = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractAsciiRuns(entry.content, {
        minLength: options.minDiagnosticAsciiRunLength,
        maxCount: options.maxDiagnosticAsciiRunsPerStream,
    });
    const needsTextAsciiRuns = /\.txt$/i.test(entry.path) || entry.path.includes('/VariantStore/');
    const textAsciiRuns = needsTextAsciiRuns ?
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractAsciiRuns(entry.content, {
            minLength: 1,
            maxCount: Number.MAX_SAFE_INTEGER,
        })
        : diagnosticAsciiRuns;
    const decodedPrimitives = OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPrimitiveRecords(entry.content, {
        streamPath: entry.path,
        streamName: entry.name,
        includeDiagnostics: options.includeDiagnostics,
        includeRawRecords: options.includeRawRecords,
    });
    const statesSheetSymbolMetadata = /^Root Entry\/(?:Packages\/[^/]+|Cache)$/.test(entry.path);
    const sheetSymbolMetadataRecords = statesSheetSymbolMetadata ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractSheetSymbolMetadataRecords(entry.content) : undefined;
    const isPageStream = streamSpecs.some((spec) => spec.name === 'page');
    const isSymbolObjectStream = streamSpecs.some((spec) => spec.name === 'cache' || spec.name === 'package' || spec.name === 'symbol');
    const contextualCatalog = options.extra.libraryStringCatalog;
    const contextualLibraryStringCatalog = Array.isArray(contextualCatalog) && contextualCatalog.every((value) => typeof value === 'string') ?
        contextualCatalog
        : undefined;
    const resolvedLibraryStringCatalog = libraryStringCatalog ?? contextualLibraryStringCatalog;
    const decodedPageObjects = isPageStream ?
        OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbPageObjects(entry.content, {
            libraryStringCatalog: resolvedLibraryStringCatalog,
            streamPath: entry.path,
            streamName: entry.name,
            includeDiagnostics: options.includeDiagnostics,
            includeRawRecords: options.includeRawRecords,
            extra: options.extra,
        })
        : undefined;
    const decodedSymbolObjects = isSymbolObjectStream ?
        OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.decodeOrCadDsnCfbSymbolObjects(entry.content, {
            streamName: entry.name,
            streamPath: entry.path,
            includeDiagnostics: options.includeDiagnostics,
            includeRawRecords: options.includeRawRecords,
        })
        : undefined;
    const diagnostics = [
        ...(decodedPrimitives.diagnostics ?? []),
        ...(decodedPageObjects?.diagnostics ?? []),
        ...(decodedSymbolObjects?.diagnostics ?? []),
    ];
    const cacheSourceLibraries = entry.path === 'Root Entry/Cache' ?
        (0, OrcadDsnCfbCacheSourceLibraries_1.decodeCacheSourceLibraries)(entry.content, decodedSymbolObjects?.records ?? [], diagnostics, decodedPrimitives.records)
        : undefined;
    const undecodedFrames = diagnostics.flatMap((diagnostic) => (diagnostic.code === 'cache-primitive-undecoded' &&
        diagnostic.offset !== undefined &&
        typeof diagnostic.details?.byteLength === 'number') ?
        [{ start: diagnostic.offset, end: diagnostic.offset + diagnostic.details.byteLength }]
        : []);
    const decisionDiagnostics = options.extra.roleDiagnostics;
    decodedPrimitives.records = decodedPrimitives.records.filter((primitive) => {
        if (!undecodedFrames.some((frame) => primitive.offset >= frame.start && primitive.offset < frame.end)) {
            return true;
        }
        decodedPrimitives.counts[primitive.kind]--;
        decodedPrimitives.decodedByteCount -= primitive.byteLength;
        return false;
    });
    const decodedRecords = [
        ...(decodedPrimitives.decodedRecords ?? []).filter((record) => !undecodedFrames.some((frame) => record.span.offset >= frame.start && record.span.offset < frame.end)),
        ...(decodedPageObjects?.decodedRecords ?? []),
        ...(decodedSymbolObjects?.decodedRecords ?? []),
    ];
    const stringOffsets = new Set(lengthPrefixedStrings.map((field) => field.offset));
    for (const source of cacheSourceLibraries ?? []) {
        for (const field of [source.library, source.continuationLibrary]) {
            if (field && !stringOffsets.has(field.offset)) {
                lengthPrefixedStrings.push(field);
                stringOffsets.add(field.offset);
            }
        }
    }
    lengthPrefixedStrings.sort((left, right) => left.offset - right.offset);
    const parsed = (entry.path === 'Root Entry/DsnStream' ? parseDsnStream(entry.content) : undefined) ??
        parseSchematicStream(entry.path, entry.content, lengthPrefixedStrings, pageStreamNames) ??
        parseLibraryStream(entry.path, entry.content, diagnostics) ??
        parseDirectoryStream(entry.path, entry.content) ??
        parseHierarchyStream(entry.path, entry.content, diagnostics) ??
        parseCisSchematicInfoStream(entry.path, entry.content) ??
        parseVariantTextStream(entry.path, textAsciiRuns) ??
        parseCacheStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
        parsePackageStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
        parseSymbolStream(entry.path, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedSymbolObjects) ??
        (decodedPageObjects ?
            parsePageStream(entry.path, entry.content, lengthPrefixedStrings, diagnosticAsciiRuns, decodedPrimitives, decodedPageObjects)
            : undefined) ??
        parseTextStream(entry.path, textAsciiRuns);
    for (const diagnostic of diagnostics) {
        if (diagnostic.code.startsWith('typed-frame-') ||
            diagnostic.code === 'cache-primitive-undecoded' ||
            diagnostic.code.startsWith('cache-source-library-') ||
            diagnostic.code === 'hierarchy-physical-name-frame-withheld' ||
            diagnostic.code === 'hierarchy-occurrence-frame-withheld' ||
            diagnostic.code === 'hierarchy-occurrence-container-withheld' ||
            diagnostic.code === 'hierarchy-child-container-withheld' ||
            diagnostic.code === 'hierarchy-payload-reference-withheld' ||
            diagnostic.code.startsWith('hierarchy-ownership-') ||
            diagnostic.code === 'placement-reference-tail-withheld' ||
            diagnostic.code === 'placement-value-withheld' ||
            diagnostic.code === 'cache-view-bounds-withheld' ||
            diagnostic.code === 'cache-pin-frame-withheld' ||
            diagnostic.code === 'page-pin-frame-withheld' ||
            diagnostic.code.startsWith('wire-alias-child-') ||
            diagnostic.code === 'page-property-frame-withheld') {
            decisionDiagnostics?.push(diagnostic);
        }
    }
    return {
        path: entry.path,
        name: entry.name,
        size: entry.size,
        storagePath: entry.storagePath,
        leadingRecords,
        lengthPrefixedStrings,
        asciiRuns: diagnosticAsciiRuns,
        ...(cacheSourceLibraries ? { cacheSourceLibraries } : {}),
        ...(sheetSymbolMetadataRecords ? { sheetSymbolMetadataRecords } : {}),
        ...(OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPrimitives(decodedPrimitives.counts) ?
            {
                decodedPrimitives: decodedPrimitives.records,
                decodedPrimitiveCounts: decodedPrimitives.counts,
                decodedPrimitivesTruncated: decodedPrimitives.truncated,
                decodedPrimitiveByteCount: decodedPrimitives.decodedByteCount,
            }
            : {}),
        ...(decodedPageObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedPageObjects(decodedPageObjects.counts) ?
            {
                decodedPageObjects: decodedPageObjects.records,
                decodedPageObjectCounts: decodedPageObjects.counts,
                decodedPageObjectsTruncated: decodedPageObjects.truncated,
                decodedPageObjectByteCount: decodedPageObjects.decodedByteCount,
            }
            : {}),
        ...(decodedSymbolObjects && OrcadDsnCfbRecordDecoders_1.OrcadDsnCfbRecordDecoders.hasDecodedSymbolObjects(decodedSymbolObjects.counts) ?
            {
                decodedSymbolObjects: decodedSymbolObjects.records,
                decodedSymbolObjectCounts: decodedSymbolObjects.counts,
                decodedSymbolObjectsTruncated: decodedSymbolObjects.truncated,
                decodedSymbolObjectByteCount: decodedSymbolObjects.decodedByteCount,
            }
            : {}),
        ...(options.includeRawRecords && decodedRecords.length > 0 ? { decodedRecords } : {}),
        diagnostics: options.includeDiagnostics ? diagnostics : (diagnostics.filter((diagnostic) => diagnostic.code.startsWith('typed-frame-') ||
            diagnostic.code.startsWith('hierarchy-ownership-') ||
            [
                'hierarchy-occurrence-frame-withheld',
                'hierarchy-occurrence-container-withheld',
                'hierarchy-ownership-frame-withheld',
                'placement-reference-tail-withheld',
                'placement-value-withheld',
                'cache-view-bounds-withheld',
                'cache-pin-frame-withheld',
                'page-pin-frame-withheld',
            ].includes(diagnostic.code))),
        ...(parsed ? { parsed } : {}),
    };
}
//# sourceMappingURL=OrcadDsnCfbStreamParsing.js.map